/**
 * As mensagens do caso em ordem (E149.F2 — GUS-149, ADR-0102 §3), a mesma lista no portal e na
 * área da equipe. Cada mensagem diz de que lado veio, o tipo, quando, e o que o tipo carrega: o
 * documento pedido e o motivo, a data informada para responder, a origem do complemento, o campo
 * apontado. A resposta diz a que item responde e o desfecho; o item que abre diz a situação.
 *
 * Quem lê do lado do cliente não recebe o e-mail de quem é da equipe (o servidor já corta).
 */
import type { ReactNode } from 'react';

import { Badge } from '@/components/ui';
import { formatarData, formatarDataHora } from '@/components/Portal/Caso/rotulos';
import { ptBR } from '@/i18n/pt-BR';
import type { MensagemData, SituacaoDoItem } from '@/lib/conversa_do_caso';

const t = ptBR.conversaDoCaso;

const TOM: Record<SituacaoDoItem, 'warning' | 'success' | 'neutral' | 'info'> = {
  aberto: 'warning',
  atendido: 'success',
  sem_documento: 'neutral',
  respondido: 'success',
  nova_versao: 'info',
  resolvido: 'success',
};

const COMPLEMENTO_DE_ARQUIVO = new Set(['complemento', 'atendido']);

function Campo({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <p className="text-caption text-fg-muted">
      <span className="font-semibold text-fg-dim">{rotulo}: </span>
      {valor}
    </p>
  );
}

function ItemRespondido({ item }: { item: MensagemData | undefined }) {
  if (item === undefined) return null;
  return (
    <p className="text-caption text-fg-dim">
      {t.campos.emRespostaA
        .replace('{{item}}', t.tipo[item.tipo])
        .replace('{{data}}', formatarDataHora(item.criada_em))}
    </p>
  );
}

function Detalhes({ m }: { m: MensagemData }) {
  const apontamento = m.tipo === 'apontamento';
  return (
    <>
      {m.material !== null ? (
        <Campo
          rotulo={apontamento ? t.campos.campoApontado : t.campos.material}
          valor={m.material}
        />
      ) : null}
      {m.motivo !== null ? <Campo rotulo={t.campos.motivo} valor={m.motivo} /> : null}
      {m.origem !== null ? <Campo rotulo={t.campos.origem} valor={m.origem} /> : null}
      {m.responder_ate !== null ? (
        <p className="text-caption text-fg-muted">
          {t.campos.responderAte.replace('{{data}}', formatarData(m.responder_ate))}
        </p>
      ) : null}
      {m.desfecho !== null ? (
        <p className="text-body-sm text-fg">{t.desfecho[m.desfecho]}</p>
      ) : null}
      {COMPLEMENTO_DE_ARQUIVO.has(m.desfecho ?? m.tipo) && m.recebimento_id !== null ? (
        <p className="text-caption text-fg-dim">{t.campos.arquivosNoCaso}</p>
      ) : null}
    </>
  );
}

function Mensagem({
  m,
  item,
  acao,
}: {
  m: MensagemData;
  item: MensagemData | undefined;
  acao: ReactNode;
}) {
  const daEquipe = m.lado === 'equipe';
  return (
    <li
      className={`border bg-surface px-4 py-3 ${daEquipe ? 'border-accent-200' : 'border-border'}`}
      data-testid={`mensagem-${m.id}`}
      data-tipo={m.tipo}
      data-lado={m.lado}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-body-sm font-semibold text-fg">
          {daEquipe ? t.quem.equipe : t.quem.cliente}
        </span>
        {m.autor !== null ? (
          <span className="font-mono text-caption text-fg-dim">{m.autor}</span>
        ) : null}
        <Badge variant={daEquipe ? 'info' : 'neutral'}>{t.tipo[m.tipo]}</Badge>
        {m.situacao !== null ? (
          <Badge variant={TOM[m.situacao]} data-testid={`situacao-${m.id}`}>
            {t.situacao[m.situacao]}
          </Badge>
        ) : null}
        <span className="ml-auto font-mono text-caption text-fg-dim">
          {formatarDataHora(m.criada_em)}
        </span>
      </div>
      <div className="mt-2 space-y-1">
        {m.responde_a !== null ? <ItemRespondido item={item} /> : null}
        {m.texto !== null ? (
          <p className="whitespace-pre-wrap text-body-sm text-fg">{m.texto}</p>
        ) : null}
        <Detalhes m={m} />
      </div>
      {acao !== null && acao !== undefined ? <div className="mt-3">{acao}</div> : null}
    </li>
  );
}

export interface ListaDeMensagensProps {
  mensagens: MensagemData[];
  /** O que quem lê pode fazer com um item aberto: responder, enviar o documento, resolver. */
  acaoDoItem?: (item: MensagemData) => ReactNode;
}

export function ListaDeMensagens({ mensagens, acaoDoItem }: ListaDeMensagensProps) {
  if (mensagens.length === 0) {
    return (
      <p className="text-body-sm text-fg-muted" data-testid="conversa-vazia">
        {t.vazio}
      </p>
    );
  }
  const porId = new Map(mensagens.map((m) => [m.id, m]));
  return (
    <ol className="space-y-3" aria-label={t.historicoRotulo} data-testid="conversa-mensagens">
      {mensagens.map((m) => (
        <Mensagem
          key={m.id}
          m={m}
          item={m.responde_a !== null ? porId.get(m.responde_a) : undefined}
          acao={m.situacao === 'aberto' && acaoDoItem !== undefined ? acaoDoItem(m) : null}
        />
      ))}
    </ol>
  );
}
